import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchCategories } from '../../../helpers/api/config/config';
import type { CategoryDef } from '../../../helpers/api/config/config';

// Single source of truth for category id -> label/color comes from the backend
// model_category_def table (GET /config/categories), the same registry the
// clinical viewer uses. Inference results emit 1-indexed category ids that match
// model_category_def.id, so we look up directly by id.
const CATEGORIES_QUERY_KEY = ['config', 'categories'] as const;

const FALLBACK_COLOR = 'rgba(255, 64, 64, 0.95)';

export interface CategoryLabels {
  labelForCategoryId: (categoryId: number) => string;
  colorForCategoryId: (categoryId: number) => string;
  isLoading: boolean;
}

export function useCategoryLabels(): CategoryLabels {
  const { data: categories = [], isLoading } = useQuery({
    queryKey: CATEGORIES_QUERY_KEY,
    queryFn: fetchCategories,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 2,
  });

  return useMemo<CategoryLabels>(() => {
    const byId = new Map<number, CategoryDef>(
      (categories as CategoryDef[]).map((c) => [c.id, c])
    );

    return {
      isLoading,
      labelForCategoryId: (categoryId: number) =>
        byId.get(categoryId)?.display_name ?? `#${categoryId}`,
      colorForCategoryId: (categoryId: number) =>
        byId.get(categoryId)?.default_color_rgba ?? FALLBACK_COLOR,
    };
  }, [categories, isLoading]);
}
